import Pager from "@/helpers/Pager";
import { merge } from "lodash";
import axios from "axios";
import { Pagination, Resource } from "@/helpers/Interfaces";

export default class DistrictsService
{
    /**
     * @param pager Pager
     * @param filter FilterModel
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(pager: Pager, filter: FilterModel): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/renters/districts', {
            params: merge({}, pager.data(), filter)
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(publicId: string): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/renters/districts/${publicId}`)).data;
    }

    /**
     * @param model FormModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(`admin/renters/districts`, model)).data;
    }

    /**
     * @param model FormModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async update(publicId: string, model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.put<Resource<FormModel>>(`admin/renters/districts/${publicId}`, model)).data;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getDistrictsAutocompleteList(search: string, limit: number = 10, citiesIds: number[] = [], showAllRecords: boolean = true): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>('admin/renters/districts/autocomplete', {
            params: {
                search: search,
                limit: limit,
                citiesIds: citiesIds.join(','),
                allRecordsVisible: showAllRecords
            }
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetchDistrictAutocompleteModel(publicId: string): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/renters/districts/autocomplete/${publicId}`)).data;
    }

    public static async getRelationsByPublicId(publicId: string): Promise<Resource<DistrictRelationsModel>>
    {
        return (await axios.get<Resource<DistrictRelationsModel>>(`admin/renters/districts/${publicId}/relations`)).data;
    }

    public static async deleteDistrictByPublicId(publicId: string): Promise<void>
    {
        await axios.delete(`admin/renters/districts/${publicId}`);
    }
}

export interface ListItemModel {
    id: number;
    publicId: string;
    cityPublicId: string;
    name: string;
    city: string;
}

export interface FormModel {
    id: number;
    publicId: string;
    cityPublicId: string;
    name: string;
}

export interface FilterModel {
    cityPublicId: string;
    districtPublicId: string;
}

export interface DistrictRelationsModel {
    id: number;
    publicId: string;
    name: string;
    city: CityInfo;
    users: UserInfo[];
    checklists: ChecklistInfo[];
    properties: PropertyInfo[];
    customProperties: CustomPropertyInfo[];
}

interface CityInfo {
    id: number;
    publicId: string;
    name: string;
}

interface UserInfo {
    id: number;
    publicId: string;
    givenName: string;
    surname: string;
}

interface ChecklistInfo {
    id: number;
    publicId: string;
    name: string;
}

interface PropertyInfo {
    id: number;
    publicId: string;
    name: string;
    address: string;
}

interface CustomPropertyInfo {
    id: number;
    publicId: string;
    name: string;
}
